/**
 * ClientApp — spec-10 Wave 4.2
 *
 * Separate React sub-app for the client portal.
 * Mounts on /client/* path via main router.tsx.
 *
 * Route guard: client_portal_user role required.
 * All pages under ClientPortalShell layout.
 */

import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useAuth } from '../../auth/AuthProvider';
import ClientPortalShell from './layouts/ClientPortalShell';

// ─── Lazy page imports ────────────────────────────────────────────────────────

const ClientLoginPage = lazy(() => import('./pages/ClientLoginPage'));
const ClientSignupPage = lazy(() => import('./pages/ClientSignupPage'));
const ClientHome = lazy(() => import('./pages/ClientHome'));
const ClientRetainer = lazy(() => import('./pages/ClientRetainer'));
const ClientMatters = lazy(() => import('./pages/ClientMatters'));
const ClientMatterDetail = lazy(() => import('./pages/ClientMatterDetail'));
const ClientDocuments = lazy(() => import('./pages/ClientDocuments'));
const ClientInvoices = lazy(() => import('./pages/ClientInvoices'));
const ClientInvoiceDetail = lazy(() => import('./pages/ClientInvoiceDetail'));
const ClientServiceRequest = lazy(() => import('./pages/ClientServiceRequest'));
const ClientProfile = lazy(() => import('./pages/ClientProfile'));
const ClientPrivacyRequest = lazy(() => import('./pages/ClientPrivacyRequest'));
const PrivacyNotice = lazy(() => import('./pages/PrivacyNotice'));

// ─── Suspense wrapper ─────────────────────────────────────────────────────────

const S: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense
    fallback={
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress size={24} sx={{ color: '#0B2545' }} />
      </Box>
    }
  >
    {children}
  </Suspense>
);

// ─── Route guard: client_portal_user only ────────────────────────────────────

function ClientPortalRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/client/login" replace />;
  }
  // If authenticated as internal user (not client_portal_user), redirect to internal portal
  if (user?.role && user.role !== 'client_portal_user') {
    // Internal users who navigate to /client are redirected to internal home
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// ─── Client app routes ────────────────────────────────────────────────────────

export const ClientAppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public login page */}
      <Route path="login" element={<S><ClientLoginPage /></S>} />

      {/* Public signup page — magic link from portal_invite.py */}
      <Route path="signup" element={<S><ClientSignupPage /></S>} />

      {/* Protected client portal — all under shell layout */}
      <Route
        path="/"
        element={
          <ClientPortalRoute>
            <ClientPortalShell />
          </ClientPortalRoute>
        }
      >
        <Route index element={<S><ClientHome /></S>} />
        <Route path="retainer" element={<S><ClientRetainer /></S>} />
        <Route path="matters" element={<S><ClientMatters /></S>} />
        <Route path="matters/:id" element={<S><ClientMatterDetail /></S>} />
        <Route path="documents" element={<S><ClientDocuments /></S>} />
        <Route path="invoices" element={<S><ClientInvoices /></S>} />
        <Route path="invoices/:id" element={<S><ClientInvoiceDetail /></S>} />
        <Route path="request" element={<S><ClientServiceRequest /></S>} />
        {/* Alias for QA finding #6 — /client/service-request navigates here */}
        <Route path="service-request" element={<S><ClientServiceRequest /></S>} />
        <Route path="profile" element={<S><ClientProfile /></S>} />
        <Route path="privacy-request" element={<S><ClientPrivacyRequest /></S>} />
        {/* spec-13 W13.2 / spec-20 J3 — privacy notice (public, no auth required) */}
        <Route path="privacy-notice" element={<S><PrivacyNotice /></S>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/client" replace />} />
      </Route>
    </Routes>
  );
};

export default ClientAppRoutes;
